// src/features/developers/components/OverviewTab.tsx
//
// «Primeros pasos»: opens with why to connect (two halves, four things to build, a way to ask for
// access), then the quick start and the rules that hold for every call.
import React from 'react';
import { useTranslate } from 'react-admin';
import { Box, Button, Card, Typography, alpha, useTheme } from '@mui/material';
import {
  ArrowDownwardRounded as SendIcon,
  ArrowUpwardRounded as ReadIcon,
  CheckCircleOutlineRounded as PointIcon,
  DashboardCustomizeOutlined as DashboardIcon,
  DomainOutlined as IntranetIcon,
  InsightsOutlined as RevenueIcon,
  HubOutlined as SystemsIcon,
  MailOutlineRounded as MailIcon,
} from '@mui/icons-material';
import { accessRequestHref } from '../contact';
import type { RenderedRecipe } from '../recipes';
import type { DeveloperInfo, DeveloperTab } from '../types';
import { CodeBlock, environmentName, InlineText, Pill } from './common';

interface OverviewTabProps {
  info: DeveloperInfo | null;
  recipes: RenderedRecipe[];
  baseUrl: string;
  go: (tab: DeveloperTab) => void;
  copiedId: string | null;
  onCopy: (id: string, text: string) => void;
}

const ENVELOPE_SAMPLE = `{
  "success": true,
  "data": [ ... ],
  "paging": { "page": 1, "pageSize": 50, "total": 128 }
}`;

const indices = (count: number): number[] => Array.from({ length: count }, (_, i) => i);

export const OverviewTab: React.FC<OverviewTabProps> = ({ info, recipes, baseUrl, go, copiedId, onCopy }) => {
  const translate = useTranslate();
  const theme = useTheme();
  const t = (key: string, options?: Record<string, unknown>): string => translate(`developers.${key}`, options);
  const render = (code: string): string => code.split('{{baseUrl}}').join(baseUrl);
  const mainCall = recipes[0];

  const cardSx = { p: { xs: 2, sm: 2.5 }, minWidth: 0 } as const;
  const h2 = { fontSize: '1.0625rem', fontWeight: 600, m: 0 } as const;
  const body = { fontSize: '0.875rem', color: theme.palette.text.secondary, lineHeight: 1.65, mt: 1 } as const;

  const bullets = (prefix: string, count: number, icon?: React.ReactNode) => (
    <Box component="ul" sx={{ listStyle: 'none', m: 0, mt: 1.5, p: 0, display: 'grid', gap: 1 }}>
      {indices(count).map((i) => (
        <Box component="li" key={i} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', fontSize: '0.875rem', color: theme.palette.text.primary, lineHeight: 1.55 }}>
          {icon && <Box sx={{ color: theme.palette.primary.dark, display: 'flex', mt: '2px', '& svg': { fontSize: 18 } }}>{icon}</Box>}
          <span>
            <InlineText text={t(`${prefix}.${i}`)} />
          </span>
        </Box>
      ))}
    </Box>
  );

  const table = (prefix: string, rowCount: number) => (
    <Box sx={{ overflowX: 'auto', mt: 1.5 }}>
      <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
        <thead>
          <tr>
            {[0, 1].map((i) => (
              <Box key={i} component="th" sx={{ textAlign: 'left', pb: 0.75, pr: 2, borderBottom: `1px solid ${theme.palette.divider}`, fontSize: '0.8125rem', fontWeight: 600, color: theme.custom.text.label }}>
                {t(`${prefix}.head.${i}`)}
              </Box>
            ))}
          </tr>
        </thead>
        <tbody>
          {indices(rowCount).map((r) => (
            <tr key={r}>
              <Box component="td" sx={{ py: 1, pr: 2, verticalAlign: 'top', whiteSpace: 'nowrap', borderBottom: `1px solid ${theme.palette.divider}` }}>
                <InlineText text={t(`${prefix}.rows.${r}.0`)} />
              </Box>
              <Box component="td" sx={{ py: 1, color: theme.palette.text.secondary, lineHeight: 1.55, borderBottom: `1px solid ${theme.palette.divider}` }}>
                <InlineText text={t(`${prefix}.rows.${r}.1`)} />
              </Box>
            </tr>
          ))}
        </tbody>
      </Box>
    </Box>
  );

  const iconTile = (icon: React.ReactNode) => (
    <Box sx={{ width: 36, height: 36, borderRadius: theme.custom.radius.small, display: 'grid', placeItems: 'center', flex: 'none', color: theme.palette.primary.dark, backgroundColor: alpha(theme.palette.primary.main, 0.1), '& svg': { fontSize: 20 } }}>
      {icon}
    </Box>
  );

  const useCases: Array<{ key: string; icon: React.ReactNode }> = [
    { key: 'bi', icon: <DashboardIcon /> },
    { key: 'intranet', icon: <IntranetIcon /> },
    { key: 'revenue', icon: <RevenueIcon /> },
    { key: 'systems', icon: <SystemsIcon /> },
  ];

  const steps: Array<{ key: string; main?: boolean; links?: Array<{ label: string; tab: DeveloperTab }> }> = [
    { key: 'access' },
    { key: 'check', main: true },
    { key: 'read', links: [{ label: t('quickStart.toRecipes'), tab: 'recipes' }] },
    { key: 'send', links: [{ label: t('quickStart.toRecipes'), tab: 'recipes' }, { label: t('quickStart.toReference'), tab: 'reference' }] },
  ];

  const requestAccess = (
    <Button
      variant="contained"
      startIcon={<MailIcon />}
      href={accessRequestHref(t('cta.subject'), t('cta.body'))}
      sx={{ whiteSpace: 'nowrap' }}
    >
      {t('cta.button')}
    </Button>
  );

  return (
    <Box sx={{ display: 'grid', gap: 2.5 }}>
      {/* Why connect */}
      <Box sx={{ maxWidth: 820 }}>
        <Typography variant="overline" sx={{ color: theme.palette.primary.dark }}>
          {t('pitch.eyebrow')}
        </Typography>
        <Typography component="h2" variant="h2" sx={{ mt: 0.25 }}>
          {t('pitch.title')}
        </Typography>
        <Typography sx={{ ...body, fontSize: '1rem' }}>{t('pitch.text')}</Typography>
      </Box>

      <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' } }}>
        {(['send', 'read'] as const).map((half) => (
          <Card key={half} sx={cardSx}>
            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
              {iconTile(half === 'send' ? <SendIcon /> : <ReadIcon />)}
              <Typography component="h3" sx={h2}>
                {t(`pitch.${half}.title`)}
              </Typography>
            </Box>
            <Typography sx={body}>{t(`pitch.${half}.text`)}</Typography>
            {bullets(`pitch.${half}.points`, 3, <PointIcon />)}
          </Card>
        ))}
      </Box>

      <Box>
        <Typography component="h2" sx={{ ...h2, mb: 1.5 }}>
          {t('useCases.title')}
        </Typography>
        <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(4, minmax(0, 1fr))' } }}>
          {useCases.map((useCase) => (
            <Card key={useCase.key} sx={cardSx}>
              {iconTile(useCase.icon)}
              <Typography component="h3" sx={{ ...h2, fontSize: '1rem', mt: 1.5 }}>
                {t(`useCases.${useCase.key}.title`)}
              </Typography>
              <Typography sx={body}>{t(`useCases.${useCase.key}.text`)}</Typography>
            </Card>
          ))}
        </Box>
      </Box>

      <Card sx={{ ...cardSx, display: 'flex', gap: 2, alignItems: { sm: 'center' }, flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', borderColor: alpha(theme.palette.primary.main, 0.4) }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography component="h2" sx={h2}>
            {t('cta.title')}
          </Typography>
          <Typography sx={body}>{t('cta.text')}</Typography>
        </Box>
        {requestAccess}
      </Card>

      {/* How */}
      <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: 'repeat(auto-fit, minmax(min(380px, 100%), 1fr))' }}>
        <Card sx={{ ...cardSx, gridColumn: '1 / -1' }}>
          <Typography component="h2" sx={h2}>
            {t('quickStart.title')}
          </Typography>
          <Box component="ol" sx={{ listStyle: 'none', m: 0, mt: 2, p: 0, display: 'grid', gap: 2.5 }}>
            {steps.map((step, i) => (
              <Box component="li" key={step.key} sx={{ display: 'grid', gridTemplateColumns: '28px minmax(0, 1fr)', gap: 1.5 }}>
                <Box sx={{ width: 26, height: 26, borderRadius: '50%', backgroundColor: alpha(theme.palette.primary.main, 0.12), color: theme.palette.primary.dark, fontWeight: 600, fontSize: '0.8125rem', display: 'grid', placeItems: 'center' }}>
                  {i + 1}
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontSize: '0.9375rem', fontWeight: 600 }}>{t(`quickStart.steps.${step.key}.title`)}</Typography>
                  <Typography sx={{ ...body, mt: 0.25 }}>{t(`quickStart.steps.${step.key}.text`)}</Typography>
                  {step.main && mainCall && <CodeBlock id="quick-start" code={render(mainCall.code.curl)} copiedId={copiedId} onCopy={onCopy} />}
                  {step.links && (
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1.25 }}>
                      {step.links.map((link) => (
                        <Button key={link.tab} size="small" variant="outlined" onClick={() => go(link.tab)}>
                          {link.label}
                        </Button>
                      ))}
                    </Box>
                  )}
                </Box>
              </Box>
            ))}
          </Box>
        </Card>

        <Card sx={cardSx}>
          <Typography component="h2" sx={h2}>
            {t('cards.environments.title')}
          </Typography>
          {(info?.environments ?? []).map((environment) => (
            <Box key={environment.key} sx={{ mt: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: '0.875rem', fontWeight: 600 }}>
                {environmentName(translate, environment)}
                {environment.key === info?.currentEnvironment && <Pill dot>{t('pills.thisEnvironment')}</Pill>}
              </Box>
              <CodeBlock id={`env-${environment.key}`} code={environment.apiBaseUrl} wrap copiedId={copiedId} onCopy={onCopy} />
            </Box>
          ))}
          {!info && <Typography sx={body}>{t('loading')}</Typography>}
          <Typography sx={{ ...body, mt: 1.5 }}>{t('cards.environments.text')}</Typography>
        </Card>

        <Card sx={cardSx}>
          <Typography component="h2" sx={h2}>
            {t('cards.authentication.title')}
          </Typography>
          {table('cards.authentication', 2)}
          <Typography sx={{ ...body, mt: 1.5 }}>{t('cards.authentication.text')}</Typography>
        </Card>

        <Card sx={cardSx}>
          <Typography component="h2" sx={h2}>
            {t('cards.requests.title')}
          </Typography>
          {bullets('cards.requests.items', 4)}
        </Card>

        <Card sx={cardSx}>
          <Typography component="h2" sx={h2}>
            {t('cards.responses.title')}
          </Typography>
          <Typography sx={body}>
            <InlineText text={t('cards.responses.text')} />
          </Typography>
          <CodeBlock id="envelope" code={ENVELOPE_SAMPLE} copiedId={copiedId} onCopy={onCopy} />
        </Card>

        <Card sx={cardSx}>
          <Typography component="h2" sx={h2}>
            {t('cards.errors.title')}
          </Typography>
          {table('cards.errors', 4)}
        </Card>

        <Card sx={cardSx}>
          <Typography component="h2" sx={h2}>
            {t('cards.limits.title')}
          </Typography>
          {bullets('cards.limits.items', 4)}
        </Card>

        <Card sx={cardSx}>
          <Typography component="h2" sx={h2}>
            {t('cards.personalData.title')}
          </Typography>
          <Typography sx={body}>{t('cards.personalData.text')}</Typography>
        </Card>

        <Card sx={cardSx}>
          <Typography component="h2" sx={h2}>
            {t('cards.assistant.title')}
          </Typography>
          <Typography sx={body}>{t('cards.assistant.text')}</Typography>
          <Button size="small" variant="outlined" onClick={() => go('downloads')} sx={{ mt: 1.5 }}>
            {t('cards.assistant.button')}
          </Button>
        </Card>
      </Box>
    </Box>
  );
};
